import { CartProductStatus } from '@notino/shared/__generated__/globalSPATypes';

import { withTryToInvokeTrackingEvent } from '@utils/exponea-logging';

import messages from './messages';
import { AddToCartProductErrors, ProductInfo } from './model';
import { AddProductToCart_AddProductToCart } from './queries/__generated__/AddProductToCart';
import {
  CartAddProductWithService_CartAddProductWithService,
  CartAddProductWithService_CartAddProductWithService_productAddResponse_products,
} from './queries/__generated__/CartAddProductWithService';

export const productPrefix = 'productsAdd';
export const servicesPrefix = 'services';

export const parseStatusesDeprecated = (
  data: CartAddProductWithService_CartAddProductWithService
) => {
  // eslint-disable-next-line no-shadow,@typescript-eslint/no-shadow
  const messages: string[] = [];
  data.productAddResponse.products.forEach((prodResponse) => {
    prodResponse.status.forEach((status) => {
      if (
        ![
          CartProductStatus.Success,
          CartProductStatus.Default,
          CartProductStatus.VoucherOnline,
        ].includes(status) &&
        !messages.includes(`${productPrefix}${status}`)
      ) {
        messages.push(`${productPrefix}${status}`);
      }
    });
  });

  if (data.serviceAddResponse) {
    data.serviceAddResponse.forEach((svcResponse) => {
      svcResponse.status.forEach((status) => {
        if (
          ![CartProductStatus.Default].includes(status) &&
          !messages.includes(
            `${servicesPrefix}${svcResponse.service.type}${status}`
          )
        ) {
          messages.push(
            `${servicesPrefix}${svcResponse.service.type}${status}`
          );
        }
      });
    });
  }
  return messages;
};

export const parseStatuses = (
  { serviceAddResponse, productAddResponse }: AddProductToCart_AddProductToCart,
  requestedCount: number
) => {
  const result: string[] = [];

  if (requestedCount !== productAddResponse.productIncrease) {
    result.push(nameof('productsAddReducedCount', messages));
  }

  if (serviceAddResponse) {
    serviceAddResponse.forEach((svcResponse) => {
      svcResponse.status.forEach((status) => {
        if (
          status !== CartProductStatus.Default &&
          !result.includes(
            `${servicesPrefix}${svcResponse.service.type}${status}`
          )
        ) {
          result.push(`${servicesPrefix}${svcResponse.service.type}${status}`);
        }
      });
    });
  }

  return result;
};

export interface INotificationMessage {
  header: string;
  messages: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values?: any;
}

function nameof<T>(key: keyof T, _instance?: T): keyof T {
  return key;
}

export const generateNotificationMessageDeprecated = (
  cartProducts: CartAddProductWithService_CartAddProductWithService_productAddResponse_products[],
  products: ProductInfo[]
): INotificationMessage => {
  // eslint-disable-next-line no-shadow,@typescript-eslint/no-shadow
  const messages = cartProducts
    .map((cartProduct) => ({
      cartProduct,
      product: products.find(
        (product) => product.id === cartProduct.id.toString()
      ),
    }))
    .map((p) =>
      generateNotificationMessageForProductDeprecated(p.cartProduct, p.product)
    );

  return messages.find((m) => m !== null) || null;
};

const generateNotificationMessageForProductDeprecated = (
  cartProduct: CartAddProductWithService_CartAddProductWithService_productAddResponse_products,
  product: ProductInfo
): INotificationMessage => {
  if (cartProduct.status.includes(CartProductStatus.OutOfPriceLimit)) {
    return {
      header: nameof('cartLimitExceededHeader', messages),
      messages: [
        nameof('cartLimitExceededExplanation', messages),
        nameof('cartLimitExceededAdvice', messages),
      ],
    };
  } else if (cartProduct.status.includes(CartProductStatus.Sold)) {
    return {
      header: nameof('cartSoldoutHeader', messages),
      messages: [nameof('cartSoldoutExplanation', messages)],
      values: { productName: `${product.name} ${product.variantName}` },
    };
  } else if (cartProduct.status.includes(CartProductStatus.VoucherOnline)) {
    return {
      header: nameof('cartOnlyVoucherHeader', messages),
      messages: [nameof('cartOnlyVoucherExplanation', messages)],
    };
  }

  return null;
};

export const generateNotificationMessageForProduct = (
  fatalErrors: string[],
  product: ProductInfo
): INotificationMessage => {
  const errors = [...fatalErrors];

  if (errors.includes(AddToCartProductErrors.SC_CART_PRICE_OUT_OF_LIMIT)) {
    return {
      header: nameof('cartLimitExceededHeader', messages),
      messages: [
        nameof('cartLimitExceededExplanation', messages),
        nameof('cartLimitExceededAdvice', messages),
      ],
    };
  }

  if (errors.includes(AddToCartProductErrors.SC_SOLD_PRODUCT)) {
    return {
      header: nameof('cartSoldoutHeader', messages),
      messages: [nameof('cartSoldoutExplanation', messages)],
      values: { productName: `${product.name} ${product.variantName}` },
    };
  }

  if (errors.includes(AddToCartProductErrors.SC_ONLINE_VOUCHER_AND_PRODUCT)) {
    return {
      header: nameof('cartOnlyVoucherHeader', messages),
      messages: [nameof('cartOnlyVoucherExplanation', messages)],
    };
  }

  if (errors.length > 0) {
    return {
      header: nameof('productsAddError', messages),
      messages: [],
    };
  }

  return null;
};

export const runTrackingEventCartUpdate = withTryToInvokeTrackingEvent(
  'CartUpdate',
  (payload: Parameters<typeof window.trackingEvents.CartUpdate>[0]) => {
    void window.trackingEvents.CartUpdate(payload);
  }
);
